<template>
  <div class="orders-container">
    <v-container fluid>
      <!-- Header con estadísticas -->
      <v-row class="mb-4">
        <v-col cols="12">
          <div class="d-flex justify-space-between align-center mb-4 orders-header">
            <h1 class="text-h4 font-weight-bold">Gestión de Órdenes</h1>
            <v-btn 
              color="primary" 
              @click="showCreateOrderDialog = true" 
              prepend-icon="mdi-plus"
              class="orders-create-btn"
            >
              <span class="d-none d-sm-inline">Nueva Orden</span>
              <span class="d-inline d-sm-none">Nueva</span>
            </v-btn>
          </div>
        </v-col>
      </v-row>

      <!-- Estadísticas del Dashboard -->
      <v-row class="mb-6">
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card">
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon color="primary" size="40" class="mr-3">mdi-cart</v-icon>
                <div>
                  <div class="text-h4 font-weight-bold">{{ dashboardStats.orders_today || 0 }}</div>
                  <div class="text-subtitle-2 text-grey">Órdenes Hoy</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card">
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon color="warning" size="40" class="mr-3">mdi-clock-outline</v-icon>
                <div>
                  <div class="text-h4 font-weight-bold">{{ dashboardStats.pending_allocation || 0 }}</div>
                  <div class="text-subtitle-2 text-grey">Pendientes Asignación</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card">
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon color="info" size="40" class="mr-3">mdi-format-list-checks</v-icon>
                <div>
                  <div class="text-h4 font-weight-bold">{{ dashboardStats.pending_picking || 0 }}</div>
                  <div class="text-subtitle-2 text-grey">Listas Picking</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card">
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon color="success" size="40" class="mr-3">mdi-percent</v-icon>
                <div>
                  <div class="text-h4 font-weight-bold">{{ dashboardStats.fulfillment_rate || 0 }}%</div>
                  <div class="text-subtitle-2 text-grey">Cumplimiento</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Filtros -->
      <v-card class="mb-4 orders-filters">
        <v-card-text>
          <v-row>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filters.status"
                :items="statusOptions"
                label="Estado"
                clearable
                @update:model-value="loadOrders"
                density="compact"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-autocomplete
                v-model="filters.customer_id"
                :items="customers"
                item-title="name"
                item-value="id"
                label="Cliente"
                clearable
                @update:model-value="loadOrders"
                density="compact"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="filters.date_from"
                type="date"
                label="Desde"
                @update:model-value="loadOrders"
                density="compact"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="filters.date_to"
                type="date"
                label="Hasta"
                @update:model-value="loadOrders"
                density="compact"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Tabla de Órdenes -->
      <v-card>
        <v-card-title>
          <span class="text-h6">Órdenes de Venta</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="loadOrders">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </v-card-title>
        
        <ResponsiveDataTable
          :headers="headers"
          :items="orders"
          :loading="loading"
          class="orders-table"
          item-key="id"
        >
          <template v-slot:item.order_number="{ item }">
            <v-btn variant="text" color="primary" @click="viewOrder(item)">
              {{ item.order_number }}
            </v-btn>
          </template>

          <template v-slot:item.status="{ item }">
            <v-chip :color="getStatusColor(item.status)" size="small">
              {{ getStatusText(item.status) }}
            </v-chip>
          </template>

          <template v-slot:item.priority="{ item }">
            <v-rating
              :model-value="item.priority"
              readonly
              size="small"
              density="compact"
            ></v-rating>
          </template>

          <template v-slot:item.total_amount="{ item }">
            ${{ formatCurrency(item.total_amount) }}
          </template>

          <template v-slot:item.order_date="{ item }">
            {{ formatDate(item.order_date) }}
          </template>

          <template v-slot:item.actions="{ item }">
            <v-menu>
              <template v-slot:activator="{ props }">
                <v-btn icon="mdi-dots-vertical" v-bind="props" size="small"></v-btn>
              </template>
              <v-list>
                <v-list-item @click="viewOrder(item)">
                  <v-list-item-title>Ver Detalles</v-list-item-title>
                </v-list-item>
                <!-- Botones temporales de debug y reparación -->
                <v-list-item @click="debugOrderAllocation(item.id)" style="color: orange;">
                  <template v-slot:prepend>
                    <v-icon>mdi-bug</v-icon>
                  </template>
                  <v-list-item-title>🔍 Debug Asignación</v-list-item-title>
                </v-list-item>
                <v-list-item @click="repairOrderState(item.id)" style="color: purple;">
                  <template v-slot:prepend>
                    <v-icon>mdi-wrench</v-icon>
                  </template>
                  <v-list-item-title>🔧 Reparar Estado</v-list-item-title>
                </v-list-item>
                <v-list-item @click="editOrder(item)" v-if="item.status === 'pending'">
                  <v-list-item-title>Editar</v-list-item-title>
                </v-list-item>
                <v-list-item @click="allocateOrder(item)" v-if="item.status === 'pending'">
                  <v-list-item-title>Asignar Inventario</v-list-item-title>
                </v-list-item>
                <v-list-item @click="createPickingList(item)" v-if="item.status === 'assigned'">
                  <v-list-item-title>Crear Lista Picking</v-list-item-title>
                </v-list-item>
                <v-list-item @click="createShipment(item)" v-if="item.status === 'picked'">
                  <v-list-item-title>Crear Envío</v-list-item-title>
                </v-list-item>
                <v-list-item @click="releaseAllocations(item)" v-if="item.status === 'assigned'">
                  <v-list-item-title>Liberar Asignaciones</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </ResponsiveDataTable>
      </v-card>
    </v-container>

    <!-- Dialog para crear/editar orden -->
    <v-dialog v-model="showCreateOrderDialog" max-width="1200px" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ editingOrder ? 'Editar Orden' : 'Nueva Orden' }}</span>
        </v-card-title>
        
        <v-card-text>
          <v-form ref="orderForm" v-model="orderFormValid">
            <v-row>
              <v-col cols="12" md="6">
                <div class="d-flex align-center gap-2">
                  <v-autocomplete
                    v-model="selectedCustomer"
                    :items="filteredCustomers"
                    item-title="display_name"
                    item-value="id"
                    label="Cliente *"
                    :rules="[v => !!v || 'Cliente es requerido']"
                    :search="customerSearch"
                    @update:search="searchCustomers"
                    @update:model-value="onCustomerSelected"
                    return-object
                    clearable
                    required
                  >
                    <template v-slot:item="{ props, item }">
                      <v-list-item v-bind="props">
                        <v-list-item-title>{{ item.raw.name }}</v-list-item-title>
                        <v-list-item-subtitle>RUT: {{ item.raw.tax_id }}</v-list-item-subtitle>
                      </v-list-item>
                    </template>
                  </v-autocomplete>
                  <v-btn 
                    color="primary" 
                    icon="mdi-plus" 
                    size="small" 
                    @click="showCreateCustomerDialog = true"
                    title="Crear nuevo cliente"
                  ></v-btn>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <v-rating
                  v-model="orderForm.priority"
                  label="Prioridad"
                  max="5"
                ></v-rating>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="orderForm.required_date"
                  type="datetime-local"
                  label="Fecha Requerida"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="orderForm.shipping_method"
                  :items="shippingMethods"
                  label="Método de Envío"
                ></v-select>
              </v-col>

                              <v-col cols="12">
                <v-switch
                  v-model="orderForm.piece_by_piece_enabled"
                  label="Picking Piece-by-Piece"
                  color="primary"
                  inset
                  :hint="orderForm.piece_by_piece_enabled ? 'Requiere escaneo de código de barras para cada producto' : 'Picking tradicional sin validación de escaneo'"
                  persistent-hint
                ></v-switch>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="orderForm.shipping_address"
                  label="Dirección de Envío"
                  rows="2"
                ></v-textarea>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="orderForm.shipping_city"
                  label="Ciudad"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="orderForm.shipping_country"
                  label="País"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="orderForm.shipping_postal_code"
                  label="Código Postal"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="orderForm.notes"
                  label="Notas"
                  rows="2"
                ></v-textarea>
              </v-col>
            </v-row>

            <!-- Estrategia de Picking -->
            <v-divider class="my-4"></v-divider>
            <!-- Estrategia heredada del producto (no se le pide al usuario) -->
            <div v-if="!mostrarOverrideEstrategia" class="d-flex align-center mb-2">
              <v-chip color="info" variant="tonal" size="small" prepend-icon="mdi-strategy">
                Estrategia automática por producto
              </v-chip>
              <v-btn
                v-if="hasPermission && hasPermission('inventory:manage')"
                variant="text"
                size="x-small"
                color="secondary"
                class="ml-2"
                @click="mostrarOverrideEstrategia = true"
              >
                Forzar estrategia
              </v-btn>
            </div>
            <PickingStrategySelector
              v-else
              v-model="pickingStrategy"
              @strategy-changed="onPickingStrategyChanged"
            />

            <!-- Líneas de Orden -->
            <v-divider class="my-4"></v-divider>
            <div class="d-flex justify-space-between align-center mb-4">
              <h3>Productos</h3>
              <v-btn color="primary" @click="addOrderLine" size="small">
                <v-icon>mdi-plus</v-icon> Agregar Producto
              </v-btn>
            </div>

            <v-card v-for="(line, index) in orderForm.order_lines" :key="index" class="mb-3">
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="4">
                    <v-autocomplete
                      v-model="line.product_id"
                      :items="getFilteredProductsForLine(index)"
                      item-title="name"
                      item-value="id"
                      label="Producto *"
                      :rules="[v => !!v || 'Producto es requerido']"
                      @update:model-value="updateLineProduct(line, $event)"
                      clearable
                      :search="line.productSearch"
                      @update:search="(search) => handleProductSearchForLine(search, index)"
                      :custom-filter="() => true"
                      :menu-props="{ maxHeight: 300, closeOnContentClick: false }"
                      :no-data-text="line.productSearch && line.productSearch.length >= 2 ? 'No se encontraron productos' : 'Escribe SKU o nombre para buscar...'"
                    >
                      <template v-slot:item="{ props, item }">
                        <v-list-item 
                          v-bind="props" 
                          :disabled="!item.raw.has_stock"
                          :class="{ 'text-grey': !item.raw.has_stock }"
                        >
                          <v-list-item-title>
                            {{ item.raw.name }}
                            <v-chip 
                              v-if="!item.raw.has_stock" 
                              size="x-small" 
                              color="error" 
                              class="ml-2"
                            >
                              Sin Stock
                            </v-chip>
                          </v-list-item-title>
                          <v-list-item-subtitle>
                            SKU: {{ item.raw.sku }} | 
                            Stock: {{ item.raw.available_quantity }} {{ item.raw.unit_of_measure }}
                          </v-list-item-subtitle>
                        </v-list-item>
                      </template>
                    </v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-text-field
                      v-model.number="line.quantity"
                      type="number"
                      label="Cantidad *"
                      :rules="[
                        v => v > 0 || 'Cantidad debe ser mayor a 0',
                        v => !line.max_quantity || v <= line.max_quantity || `Máximo disponible: ${line.max_quantity}`
                      ]"
                      :max="line.max_quantity"
                      @update:model-value="calculateLineTotal(line)"
                      :hint="getQuantityHint(line)"
                      persistent-hint
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-text-field
                      v-model.number="line.unit_price"
                      type="number"
                      step="0.01"
                      label="Precio Unitario *"
                      :rules="[v => v > 0 || 'Precio debe ser mayor a 0']"
                      @update:model-value="calculateLineTotal(line)"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-text-field
                      :model-value="line.line_total"
                      label="Total Línea"
                      readonly
                      prefix="$"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-btn
                      color="error"
                      icon="mdi-delete"
                      @click="removeOrderLine(index)"
                      size="small"
                    ></v-btn>
                  </v-col>
                </v-row>
                
                <!-- Selector de Estrategia Individual (solo con override avanzado) -->
                <div v-if="mostrarOverrideEstrategia && pickingStrategy.level === 'product' && line.product_id" class="mt-4">
                  <v-card variant="outlined" class="pa-3">
                    <div class="d-flex align-center mb-3">
                      <v-icon color="secondary" class="mr-2">mdi-strategy</v-icon>
                      <span class="text-subtitle-1 font-weight-medium">Estrategia para este Producto</span>
                    </div>
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-select
                          :key="`strategy-${index}-${line.product_id}`"
                          v-model="line.picking_strategy"
                          :items="getStrategyOptionsForProduct(line)"
                          item-title="text"
                          item-value="value"
                          label="Estrategia de Picking"
                          @update:model-value="onProductStrategyChanged(index, $event)"
                          clearable
                          variant="outlined"
                          density="compact"
                        >
                          <template v-slot:item="{ props, item }">
                            <v-list-item v-bind="props">
                              <v-list-item-title>{{ item.raw.text }}</v-list-item-title>
                              <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                            </v-list-item>
                          </template>
                        </v-select>
                      </v-col>
                    </v-row>
                    
                    <!-- Alerta si no hay criterios para estrategias automáticas -->
                    <v-alert
                      v-if="line.criteria_error"
                      type="warning"
                      variant="tonal"
                      class="mt-2"
                      :text="line.criteria_error"
                    ></v-alert>
                  </v-card>
                </div>

                <!-- Selectores de Criterios Granulares -->
                <div v-if="showCriteriaSelectors(line, index)" class="mt-4">
                  <PickingCriteriaSelector
                    :key="`criteria-${index}-${line.product_id}-${line.available_lots.length}`"
                    v-model="line.picking_criteria"
                    :available-lots="line.available_lots"
                    :available-serials="line.available_serials"
                    :available-expiry-dates="line.available_expiry_dates"
                    :used-lots="getUsedLots(line.product_id, index)"
                    :error-message="line.criteria_error"
                    @criteria-changed="onCriteriaChanged(index, $event)"
                  />
                </div>

                <!-- Información del Producto Seleccionado -->
                <div v-if="getSelectedProductInfo(line)" class="mt-4">
                  <v-card variant="outlined" class="pa-3" color="success" style="border-color: #4CAF50;">
                    <div class="d-flex align-center mb-3">
                      <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
                      <span class="text-subtitle-1 font-weight-medium text-success">Producto Seleccionado</span>
                    </div>
                    
                    <v-row>
                      <v-col cols="12" md="3">
                        <div class="text-caption text-grey-600">Estrategia</div>
                        <div class="text-body-2 font-weight-medium">
                          {{ getSelectedProductInfo(line).strategyName }}
                        </div>
                      </v-col>
                      
                      <v-col cols="12" md="3" v-if="getSelectedProductInfo(line).criteriaInfo">
                        <div class="text-caption text-grey-600">{{ getSelectedProductInfo(line).criteriaLabel }}</div>
                        <div class="text-body-2 font-weight-medium">
                          {{ getSelectedProductInfo(line).criteriaInfo }}
                        </div>
                      </v-col>
                      
                      <v-col cols="12" md="3" v-if="getSelectedProductInfo(line).dateInfo">
                        <div class="text-caption text-grey-600">{{ getSelectedProductInfo(line).dateLabel }}</div>
                        <div class="text-body-2 font-weight-medium">
                          {{ getSelectedProductInfo(line).dateInfo }}
                        </div>
                      </v-col>
                      
                      <v-col cols="12" md="3" v-if="getSelectedProductInfo(line).location">
                        <div class="text-caption text-grey-600">Ubicación</div>
                        <div class="text-body-2 font-weight-medium">
                          <v-icon size="small" class="mr-1">mdi-map-marker</v-icon>
                          {{ getSelectedProductInfo(line).location }}
                        </div>
                      </v-col>
                    </v-row>
                  </v-card>
                </div>
              </v-card-text>
            </v-card>

            <!-- Totales -->
            <v-card class="mt-4">
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="8"></v-col>
                  <v-col cols="12" md="4">
                    <div class="text-right">
                      <div class="d-flex justify-space-between">
                        <span>Subtotal:</span>
                        <span>${{ formatCurrency(calculateSubtotal()) }}</span>
                      </div>
                      <div class="d-flex justify-space-between">
                        <span>IVA (19%):</span>
                        <span>${{ formatCurrency(calculateTax()) }}</span>
                      </div>
                      <v-divider class="my-2"></v-divider>
                      <div class="d-flex justify-space-between text-h6 font-weight-bold">
                        <span>Total:</span>
                        <span>${{ formatCurrency(calculateTotal()) }}</span>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="cancelOrderDialog">Cancelar</v-btn>
          <v-btn color="primary" @click="saveOrder" :disabled="!orderFormValid">
            {{ editingOrder ? 'Actualizar' : 'Crear' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog para ver detalles de orden -->
    <v-dialog v-model="showOrderDetailsDialog" max-width="1000px">
      <v-card v-if="selectedOrder">
        <v-card-title>
          <span class="text-h5">Orden {{ selectedOrder.order_number }}</span>
          <v-spacer></v-spacer>
          <v-chip :color="getStatusColor(selectedOrder.status)">
            {{ getStatusText(selectedOrder.status) }}
          </v-chip>
        </v-card-title>
        
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <h4>Información del Cliente</h4>
              <p><strong>Cliente:</strong> {{ selectedOrder.customer_name }}</p>
              <p><strong>Fecha Orden:</strong> {{ formatDate(selectedOrder.order_date) }}</p>
              <p><strong>Fecha Requerida:</strong> {{ formatDate(selectedOrder.required_date) }}</p>
              <p><strong>Prioridad:</strong> {{ selectedOrder.priority }}/5</p>
            </v-col>
            <v-col cols="12" md="6">
              <h4>Información de Envío</h4>
              <p><strong>Dirección:</strong> {{ selectedOrder.shipping_address }}</p>
              <p><strong>Ciudad:</strong> {{ selectedOrder.shipping_city }}</p>
              <p><strong>País:</strong> {{ selectedOrder.shipping_country }}</p>
              <p><strong>Método:</strong> {{ selectedOrder.shipping_method }}</p>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>
          
          <h4>Productos</h4>
          <v-data-table
            :headers="orderLineHeaders"
            :items="selectedOrder.order_lines"
            hide-default-footer
            class="mt-2"
          >
            <template v-slot:item.line_total="{ item }">
              ${{ formatCurrency(item.line_total) }}
            </template>
            <template v-slot:item.unit_price="{ item }">
              ${{ formatCurrency(item.unit_price) }}
            </template>
          </v-data-table>

          <v-row class="mt-4">
            <v-col cols="12" md="8"></v-col>
            <v-col cols="12" md="4">
              <div class="text-right">
                <div class="d-flex justify-space-between">
                  <span>Subtotal:</span>
                  <span>${{ formatCurrency(selectedOrder.subtotal) }}</span>
                </div>
                <div class="d-flex justify-space-between">
                  <span>IVA:</span>
                  <span>${{ formatCurrency(selectedOrder.tax_amount) }}</span>
                </div>
                <v-divider class="my-2"></v-divider>
                <div class="d-flex justify-space-between text-h6 font-weight-bold">
                  <span>Total:</span>
                  <span>${{ formatCurrency(selectedOrder.total_amount) }}</span>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showOrderDetailsDialog = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog para crear cliente -->
    <v-dialog v-model="showCreateCustomerDialog" max-width="800px" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h5">Nuevo Cliente</span>
        </v-card-title>
        
        <v-card-text>
          <v-form ref="customerForm" v-model="customerFormValid">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="customerForm.name"
                  label="Nombre o Razón Social *"
                  :rules="[v => !!v || 'Nombre es requerido']"
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="customerForm.tax_id"
                  label="RUT *"
                  :rules="[v => !!v || 'RUT es requerido']"
                  required
                  placeholder="12.345.678-9"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="customerForm.contact_person"
                  label="Persona de Contacto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="customerForm.contact_phone"
                  label="Teléfono de Contacto"
                  placeholder="+56 9 1234 5678"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="customerForm.contact_email"
                  label="E-mail de Contacto"
                  type="email"
                  placeholder="contacto@empresa.com"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="customerForm.city"
                  label="Comuna"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="customerForm.address"
                  label="Dirección"
                  rows="2"
                ></v-textarea>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="customerForm.country"
                  label="País"
                  placeholder="Chile"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="customerForm.postal_code"
                  label="Código Postal"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="customerForm.payment_terms"
                  label="Términos de Pago"
                  placeholder="30 días"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="cancelCustomerDialog">Cancelar</v-btn>
          <v-btn color="primary" @click="saveCustomer" :disabled="!customerFormValid">
            Crear Cliente
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar para notificaciones -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000">
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import api from '@/api'
import { mapGetters } from 'vuex'
import ResponsiveDataTable from '@/components/ResponsiveDataTable.vue'
import PickingStrategySelector from '@/components/PickingStrategySelector.vue'
import PickingCriteriaSelector from '@/components/PickingCriteriaSelector.vue'

export default {
  name: 'Orders',
  components: {
    ResponsiveDataTable,
    PickingStrategySelector,
    PickingCriteriaSelector
  },
  data() {
    return {
      loading: false,
      orders: [],
      customers: [],
      products: [],
      dashboardStats: {},
      
      // Filtros
      filters: {
        status: null,
        customer_id: null,
        date_from: null,
        date_to: null
      },
      
      // Dialogs
      showCreateOrderDialog: false,
      showOrderDetailsDialog: false,
      editingOrder: null,
      selectedOrder: null,
      
      // Form
      orderFormValid: false,
      orderForm: {
        customer_id: null,
        priority: 1,
        required_date: null,
        shipping_address: '',
        shipping_city: '',
        shipping_country: '',
        shipping_postal_code: '',
        shipping_method: null,
        picking_strategy: null,
        piece_by_piece_enabled: false,
        notes: '',
        order_lines: []
      },
      
      // Opciones
      statusOptions: [
        { title: 'Pendiente', value: 'pending' },
        { title: 'Asignado', value: 'assigned' },
        { title: 'Recolectando', value: 'picking' },
        { title: 'Recolectado', value: 'picked' },
        { title: 'Empacado', value: 'packed' },
        { title: 'Pickeado', value: 'shipped' },
        { title: 'Entregado', value: 'delivered' },
        { title: 'Cancelado', value: 'cancelled' }
      ],
      
      shippingMethods: [
        'Estándar',
        'Express',
        'Overnight',
        'Retiro en Tienda'
      ],
      
      // Headers de tabla
      headers: [
        { title: 'Número', key: 'order_number', sortable: true },
        { title: 'Cliente', key: 'customer_name', sortable: true },
        { title: 'Estado', key: 'status', sortable: true },
        { title: 'Prioridad', key: 'priority', sortable: true },
        { title: 'Fecha', key: 'order_date', sortable: true },
        { title: 'Total', key: 'total_amount', sortable: true },
        { title: 'Acciones', key: 'actions', sortable: false }
      ],
      
      orderLineHeaders: [
        { title: 'Producto', key: 'product_name' },
        { title: 'SKU', key: 'product_sku' },
        { title: 'Cantidad', key: 'quantity' },
        { title: 'Precio Unit.', key: 'unit_price' },
        { title: 'Total', key: 'line_total' }
      ],
      
      // Snackbar
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      },
      
      // Customer search
      customerSearch: '',
      filteredCustomers: [],
      showCreateCustomerDialog: false,
      productsWithInventory: [],
      filteredProductsByLineIndex: {},
      
      // NUEVOS CAMPOS para picking granular. Por defecto 'product': cada línea
      // hereda la estrategia de su producto y no se le pide al usuario.
      pickingStrategy: {
        level: 'product', // 'order' o 'product'
        strategy: null, // 'fifo', 'lifo', 'fefo', 'manual', null
      },
      mostrarOverrideEstrategia: false,
      
      // Cache de items disponibles por producto
      availableItemsCache: {},
      
      // Validaciones de lotes usados (para evitar duplicados)
      usedLotsByProduct: {},
      
      customerFormValid: false,
      customerForm: {
        name: '',
        tax_id: '',
        contact_person: '',
        contact_phone: '',
        contact_email: '',
        city: '',
        address: '',
        country: '',
        postal_code: '',
        payment_terms: ''
      },
      pickingStrategyOptions: [
        { text: 'Estrategia 1', value: 'strategy1' },
        { text: 'Estrategia 2', value: 'strategy2' },
        { text: 'Estrategia 3', value: 'strategy3' }
      ]
    }
  },
  
  computed: {
    ...mapGetters('auth', ['hasPermission']),

    selectedCustomer: {
      get() {
        if (!this.orderForm.customer_id || !this.customers.length) return null
        return this.customers.find(customer => customer.id === this.orderForm.customer_id) || null
      },
      set(customer) {
        this.orderForm.customer_id = customer ? customer.id : null
      }
    }
  },
  
  async mounted() {
    await this.loadInitialData()
  },
  
  methods: {
    async loadInitialData() {
      await Promise.all([
        this.loadOrders(),
        this.loadCustomers(),
        this.loadProducts(),
        this.loadDashboardStats(),
        this.loadPickingStrategies()
      ])
    },
    
    async loadOrders() {
      this.loading = true
      try {
        const params = {}
        Object.keys(this.filters).forEach(key => {
          if (this.filters[key]) {
            params[key] = this.filters[key]
          }
        })
        
        const response = await api.getOrders(params)
        this.orders = response.data.orders || response.data
      } catch (error) {
        this.showSnackbar('Error al cargar órdenes', 'error')
        console.error('Error loading orders:', error)
      } finally {
        this.loading = false
      }
    },
    
    async loadCustomers() {
      try {
        const response = await api.getCustomers()
        this.customers = response.data.map(customer => ({
          ...customer,
          display_name: `${customer.name} (${customer.tax_id})`
        }))
        this.filteredCustomers = this.customers
      } catch (error) {
        console.error('Error loading customers:', error)
      }
    },
    
    async loadProducts() {
      try {
        const response = await api.getProductsWithInventory()
        this.productsWithInventory = response.data
        this.products = response.data // Mantener compatibilidad
      } catch (error) {
        console.error('Error loading products:', error)
      }
    },
    
    async loadDashboardStats() {
      try {
        const response = await api.getOrdersDashboardStats()
        this.dashboardStats = response.data
      } catch (error) {
        console.error('Error loading dashboard stats:', error)
      }
    },
    
    async loadPickingStrategies() {
      try {
        const response = await api.getPickingStrategies()
        if (response.data && response.data.strategies) {
          this.pickingStrategyOptions = response.data.strategies.map(strategy => ({
            text: strategy.description || strategy.name,
            value: strategy.value
          }))
        }
      } catch (error) {
        console.error('Error loading picking strategies:', error)
        // Mantener estrategias por defecto si falla la carga
      }
    },

    // ========== FUNCIONES DE DEBUG Y REPARACIÓN ==========

    async repairOrderState(orderId) {
      try {
        console.log('🔧 ===== REPARANDO ESTADO DE ORDEN =====')
        console.log('🆔 Order ID:', orderId)
        
        const response = await api.get(`/orders/${orderId}/repair-state`, {})
        
        if (response.data.success) {
          console.log('✅ Estado de orden reparado exitosamente')
          this.showSnackbar(`Orden ${orderId} reparada: ${response.data.message}`, 'success', 5000)
          await this.loadOrders() // Recargar la lista
        } else {
          this.showSnackbar('Error al reparar estado de orden', 'error')
        }
        
      } catch (error) {
        console.error('❌ Error reparando estado de orden:', error)
        this.showSnackbar('Error al reparar estado de orden', 'error')
      }
    },
    
    // ========== FUNCIÓN DE DEBUG PARA ASIGNACIÓN DE INVENTARIO ==========
    async debugOrderAllocation(orderId) {
      try {
        console.log('🔍 ===== DEBUG ORDER ALLOCATION =====')
        console.log('🆔 Order ID:', orderId)
        
        // 1. Obtener detalles de la orden
        const orderResponse = await api.getOrder(orderId)
        const order = orderResponse.data
        
        console.log('📋 Order status:', order.status)
        console.log('📋 Order state:', order.state)
        console.log('📋 Order lines:', order.order_lines?.length)
        console.log('🔍 RAW ORDER RESPONSE:', JSON.stringify(orderResponse.data, null, 2))
        
        // DIAGNÓSTICO ESPECÍFICO PARA ERROR 400
        console.log('🚨 DIAGNÓSTICO ERROR 400:')
        console.log('🚨 Order status:', order.status)
        console.log('🚨 Expected status: assigned')
        console.log('🚨 Status matches ASSIGNED?', order.status === 'assigned')
        
        if (order.status !== 'assigned') {
          console.log('❌ PROBLEMA: La orden NO está en estado ASSIGNED')
          console.log('❌ Estado actual:', order.status) 
          console.log('❌ Para crear picking list, debe estar en ASSIGNED')
        }
        
        // 2. Analizar líneas de orden
        if (order.order_lines) {
          console.log('🔍 ANÁLISIS DE LÍNEAS:')
          order.order_lines.forEach((line, index) => {
            console.log(`📦 Línea ${index + 1}:`, {
              product: line.product?.name || `ID: ${line.product_id}`,
              requested_qty: line.quantity,
              allocated_qty: line.allocated_quantity || 0,
              has_allocations: (line.allocated_quantity || 0) > 0,
              allocation_details: line.allocations || 'No allocation details'
            })
          })
          
          // 3. Verificar si hay allocations
          const totalRequested = order.order_lines.reduce((sum, line) => sum + line.quantity, 0)
          const totalAllocated = order.order_lines.reduce((sum, line) => sum + (line.allocated_quantity || 0), 0)
          const isFullyAllocated = totalAllocated >= totalRequested
          
          console.log('📊 RESUMEN DE ASIGNACIÓN:')
          console.log('📊 Total solicitado:', totalRequested)
          console.log('📊 Total asignado:', totalAllocated)
          console.log('📊 Completamente asignado:', isFullyAllocated)
          console.log('📊 Puede crear picking list:', isFullyAllocated ? '✅ SÍ' : '❌ NO')
          
          // 4. Mostrar en interfaz
          const message = `DIAGNÓSTICO DE ORDEN ${orderId}: Estado: ${order.status}, Total solicitado: ${totalRequested}, Total asignado: ${totalAllocated}, ${isFullyAllocated ? '✅ OK' : '❌ FALTA INVENTARIO'}`
          
          this.showSnackbar(message, isFullyAllocated ? 'success' : 'error', 8000)
        }
        
      } catch (error) {
        console.error('❌ Error debugging order allocation:', error)
        this.showSnackbar('Error al verificar asignación de inventario', 'error')
      }
    },
    
    // Gestión de órdenes
    viewOrder(order) {
      this.selectedOrder = order
      this.showOrderDetailsDialog = true
    },
    
    async editOrder(order) {
      this.editingOrder = order
      this.orderForm = {
        customer_id: order.customer_id,
        priority: order.priority,
        required_date: order.required_date ? order.required_date.slice(0, 16) : null,
        shipping_address: order.shipping_address || '',
        shipping_city: order.shipping_city || '',
        shipping_country: order.shipping_country || '',
        shipping_postal_code: order.shipping_postal_code || '',
        shipping_method: order.shipping_method,
        picking_strategy: order.picking_strategy,
        piece_by_piece_enabled: order.piece_by_piece_enabled,
        notes: order.notes || '',
        order_lines: order.order_lines.map(line => ({
          product_id: line.product_id,
          quantity: line.quantity,
          unit_price: line.unit_price,
          line_total: line.line_total,
          // Inicializar campos granulares vacíos
          picking_strategy: null,
          picking_criteria: { type: null, value: null, available_quantity: 0 },
          available_lots: [],
          available_serials: [],
          available_expiry_dates: [],
          has_any_criteria: false,
          can_select_criteria: false,
          criteria_error: null,
          max_quantity: line.quantity
        }))
      }
      
      // Cargar criterios granulares si la orden tiene asignaciones
      try {
        console.log(`🔍 Loading granular criteria for order ${order.id}`)
        const criteriaResponse = await api.getOrderGranularCriteria(order.id)
        const { picking_strategy_config, granular_criteria } = criteriaResponse.data
        
        console.log(`📋 Backend response:`, { picking_strategy_config, granular_criteria })
        
        // Configurar estrategia de picking
        this.pickingStrategy = picking_strategy_config
        console.log(`🎯 Set picking strategy:`, this.pickingStrategy)
        
        // Configurar criterios por línea (debe ser async para esperar la carga de datos)
        for (let index = 0; index < this.orderForm.order_lines.length; index++) {
          const line = this.orderForm.order_lines[index]
          const orderLine = order.order_lines[index]
          
          if (orderLine && granular_criteria[orderLine.id]) {
            const criteria = granular_criteria[orderLine.id]
            
            console.log(`🔍 Loading criteria for line ${index}:`, criteria)
            
            // Asignar estrategia individual si es por producto
            if (picking_strategy_config.level === 'product') {
              line.picking_strategy = criteria.picking_strategy
              console.log(`📋 Set individual strategy for line ${index}:`, criteria.picking_strategy)
            }
            
            // Primero cargar datos disponibles para el producto
            await this.loadAvailableItemsForLine(line, index, line.product_id)
            
            // Esperar un tick para asegurar que los datos estén completamente cargados
            await this.$nextTick()
            
            // Luego asignar criterios específicos (después de que los datos estén disponibles)
            line.picking_criteria = {
              type: criteria.type,
              value: criteria.value,
              available_quantity: criteria.available_quantity
            }
            
            console.log(`🎯 Set criteria for line ${index}:`, line.picking_criteria)
            console.log(`🔍 Line state after setting criteria:`, {
              available_lots: line.available_lots,
              available_serials: line.available_serials,
              has_any_criteria: line.has_any_criteria
            })
          }
        }
        
        console.log('🔄 Criterios granulares cargados para edición:', { picking_strategy_config, granular_criteria })
        
      } catch (error) {
        console.error('Error cargando criterios granulares:', error)
        // Continuar sin criterios granulares si hay error
      }
      
      this.showCreateOrderDialog = true
    },
    
    async saveOrder() {
      if (!this.orderFormValid) return
      
      try {
        const orderData = { ...this.orderForm }
        
        // Agregar información de picking granular
        orderData.picking_strategy_config = {
          level: this.pickingStrategy.level,
          strategy: this.pickingStrategy.strategy
        }
        
        // Agregar criterios granulares por línea
        orderData.granular_criteria = {}
        this.orderForm.order_lines.forEach((line, index) => {
          console.log(`🔍 Line ${index} data:`, {
            product_id: line.product_id,
            picking_strategy: line.picking_strategy,
            picking_criteria: line.picking_criteria,
            has_any_criteria: line.has_any_criteria
          })
          
          if (line.picking_criteria && (line.picking_criteria.type || line.picking_strategy)) {
            orderData.granular_criteria[index] = {
              picking_strategy: line.picking_strategy,
              type: line.picking_criteria.type,
              value: line.picking_criteria.value,
              available_quantity: line.picking_criteria.available_quantity
            }
            console.log(`✅ Added granular criteria for line ${index}:`, orderData.granular_criteria[index])
          } else {
            console.log(`❌ No granular criteria for line ${index} - missing data`)
          }
        })
        
        console.log('📦 Saving order with granular criteria:', orderData.granular_criteria)
        console.log('🎯 Picking strategy config:', orderData.picking_strategy_config)
        
        if (this.editingOrder) {
          await api.updateOrder(this.editingOrder.id, orderData)
          this.showSnackbar('Orden actualizada exitosamente')
        } else {
          const response = await api.createOrder(orderData)
          this.showSnackbar('Orden creada exitosamente')
          
          // Auto-asignar inventario si hay criterios granulares
          if (Object.keys(orderData.granular_criteria).length > 0 || orderData.picking_strategy_config.strategy) {
            try {
              console.log('🔄 Auto-asignando inventario granular...')
              const allocationResult = await this.autoAllocateGranularInventory(response.data.id, orderData)
              
              // Si la asignación fue exitosa y completa, la orden ya está en estado ASSIGNED
              if (allocationResult && allocationResult.fully_allocated) {
                this.showSnackbar('Orden creada y inventario asignado automáticamente', 'success')
              } else {
                this.showSnackbar('Orden creada con asignación parcial', 'warning')
              }
            } catch (allocError) {
              console.error('Error auto-asignando inventario:', allocError)
              this.showSnackbar('Orden creada, pero error al asignar inventario', 'warning')
            }
          } else {
            // Si no hay criterios granulares, la orden queda en estado PENDING
            this.showSnackbar('Orden creada (sin asignación automática)', 'info')
          }
        }
        
        this.cancelOrderDialog()
        await this.loadOrders()
        await this.loadDashboardStats()
      } catch (error) {
        this.showSnackbar('Error al guardar orden', 'error')
        console.error('Error saving order:', error)
      }
    },
    
    async autoAllocateGranularInventory(orderId, orderData) {
      try {
        // Preparar datos para asignación granular
        const allocationData = {
          granular_criteria: {},
          picking_strategy: orderData.picking_strategy_config
        }
        
        // Convertir índices de líneas a IDs de líneas (necesitamos hacer una consulta para obtener los IDs)
        const orderResponse = await api.getOrder(orderId)
        const orderLines = orderResponse.data.order_lines
        
        // Mapear criterios granulares con IDs reales de líneas
        Object.keys(orderData.granular_criteria).forEach(index => {
          const lineIndex = parseInt(index)
          if (orderLines[lineIndex]) {
            allocationData.granular_criteria[orderLines[lineIndex].id] = orderData.granular_criteria[index]
          }
        })
        
        console.log('🎯 Allocation data:', allocationData)
        console.log('🔍 Granular criteria count:', Object.keys(allocationData.granular_criteria).length)
        console.log('🔍 Picking strategy:', allocationData.picking_strategy)
        
        // Llamar al endpoint de asignación granular
        const response = await api.allocateOrder(orderId, allocationData)
        
        console.log('📨 Allocation response:', response.data)
        
        if (response.data.success) {
          console.log('✅ Inventario asignado exitosamente:', response.data)
          
          // Verificar que realmente se asignó inventario
          setTimeout(async () => {
            try {
              const verifyResponse = await api.getOrder(orderId)
              const hasAllocations = verifyResponse.data.order_lines?.some(line => 
                line.allocated_quantity && line.allocated_quantity > 0
              )
              console.log('🔍 Verificación post-asignación - Tiene allocations:', hasAllocations)
              console.log('🔍 Order lines status:', verifyResponse.data.order_lines?.map(line => ({
                product: line.product?.name,
                requested: line.quantity,
                allocated: line.allocated_quantity || 0
              })))
            } catch (verifyError) {
              console.error('❌ Error verificando asignación:', verifyError)
            }
          }, 1000)
          
          return response.data
        } else {
          throw new Error('Error en la asignación de inventario')
        }
        
      } catch (error) {
        console.error('❌ Error auto-asignando inventario:', error)
        throw error
      }
    },
    
    cancelOrderDialog() {
      this.showCreateOrderDialog = false
      this.editingOrder = null
      this.mostrarOverrideEstrategia = false
      this.pickingStrategy = { level: 'product', strategy: null }
      this.orderForm = {
        customer_id: null,
        priority: 1,
        required_date: null,
        shipping_address: '',
        shipping_city: '',
        shipping_country: '',
        shipping_postal_code: '',
        shipping_method: null,
        picking_strategy: null,
        piece_by_piece_enabled: false,
        notes: '',
        order_lines: []
      }
    },
    
    // Gestión de líneas de orden
    addOrderLine() {
      const index = this.orderForm.order_lines.length
      this.orderForm.order_lines.push({
        product_id: null,
        quantity: 1,
        unit_price: 0,
        line_total: 0,
        max_quantity: 0,
        productSearch: '',
        
        // NUEVOS CAMPOS para picking granular
        picking_strategy: null, // Solo si pickingStrategy.level === 'product'
        picking_criteria: {
          type: null, // 'lot', 'serial', 'expiry'
          value: null, // valor seleccionado
          available_quantity: 0 // cantidad disponible para ese criterio
        },
        
        // Datos disponibles para selectores
        available_lots: [],
        available_serials: [],
        available_expiry_dates: [],
        has_any_criteria: false,
        
        // Validaciones
        can_select_criteria: true,
        criteria_error: null
      })
      // Inicializar el filtro para esta línea
      this.filteredProductsByLineIndex[index] = this.productsWithInventory
    },
    
    removeOrderLine(index) {
      this.orderForm.order_lines.splice(index, 1)
      // Limpiar el filtro para este índice
      delete this.filteredProductsByLineIndex[index]
      // Reindexar los filtros restantes
      const newFilters = {}
      Object.keys(this.filteredProductsByLineIndex).forEach(key => {
        const keyIndex = parseInt(key)
        if (keyIndex > index) {
          newFilters[keyIndex - 1] = this.filteredProductsByLineIndex[key]
        } else if (keyIndex < index) {
          newFilters[keyIndex] = this.filteredProductsByLineIndex[key]
        }
      })
      this.filteredProductsByLineIndex = newFilters
    },
    
    updateLineProduct(line, productId) {
      const index = this.orderForm.order_lines.indexOf(line)
      this.onProductSelected(line, index, productId)
    },
    
    calculateLineTotal(line) {
      line.line_total = (line.quantity || 0) * (line.unit_price || 0)
    },
    
    calculateSubtotal() {
      return this.orderForm.order_lines.reduce((sum, line) => sum + (line.line_total || 0), 0)
    },
    
    calculateTax() {
      return this.calculateSubtotal() * 0.19
    },
    
    calculateTotal() {
      return this.calculateSubtotal() + this.calculateTax()
    },
    
    // Filtrado de productos
    getFilteredProductsForLine(index) {
      return this.filteredProductsByLineIndex[index] || this.productsWithInventory
    },
    
    handleProductSearchForLine(search, index) {
      const line = this.orderForm.order_lines[index]
      if (line) {
        line.productSearch = search
      }
      
      if (!search || search.length < 2) {
        this.filteredProductsByLineIndex[index] = this.productsWithInventory
        return
      }
      
      const filtered = this.productsWithInventory.filter(product => {
        const searchLower = search.toLowerCase()
        const skuMatch = product.sku && product.sku.toLowerCase().includes(searchLower)
        const nameMatch = product.name && product.name.toLowerCase().includes(searchLower)
        console.log(`🔍 Filtering "${search}" - Product: ${product.name}, SKU: ${product.sku}, SKU Match: ${skuMatch}, Name Match: ${nameMatch}`)
        return skuMatch || nameMatch
      })
      
      this.filteredProductsByLineIndex[index] = filtered
      console.log(`🔍 Filtered ${filtered.length} products for "${search}"`)
      
      // Auto-seleccionar si hay exactamente 1 resultado y búsqueda >= 4 caracteres
      if (filtered.length === 1 && search.length >= 4 && line) {
        setTimeout(() => {
          line.product_id = filtered[0].id
          this.updateLineProduct(line, filtered[0].id)
        }, 100)
      }
    },
    
    // ==================== PICKING GRANULAR ====================
    
    // Verificar si se deben mostrar selectores de criterios
    showCriteriaSelectors(line, index) {
      // Estrategia por orden/tarea: mostrar si es manual
      if (this.pickingStrategy.level === 'order') {
        return this.pickingStrategy.strategy === 'manual'
      }
      
      // Estrategia por producto: mostrar si la estrategia individual es manual
      if (this.pickingStrategy.level === 'product') {
        return line.picking_strategy === 'manual'
      }
      
      return false
    },
    
    // Manejar cambios en la estrategia de picking
    onPickingStrategyChanged(event) {
      console.log('🎯 Picking strategy changed:', event)
      
      if (event.type === 'level-changed') {
        if (event.level === 'order') {
          // Limpiar estrategias individuales de productos
          this.orderForm.order_lines.forEach(line => {
            line.picking_strategy = null
            this.clearLineCriteria(line)
          })
        } else if (event.level === 'product') {
          // Limpiar criterios de todas las líneas para permitir selección individual
          this.orderForm.order_lines.forEach(line => {
            this.clearLineCriteria(line)
          })
        }
      }
      
      if (event.type === 'strategy-changed' && event.level === 'order') {
        // Aplicar estrategia automática a todos los productos si no es manual
        if (event.strategy && event.strategy !== 'manual') {
          this.orderForm.order_lines.forEach((line, index) => {
            if (line.product_id) {
              this.applyAutomaticStrategy(line, event.strategy, index)
            }
          })
        } else {
          // Si es manual o se limpia, limpiar criterios
          this.orderForm.order_lines.forEach(line => {
            this.clearLineCriteria(line)
          })
        }
      }
    },
    
    // Estrategia heredada del producto (default FEFO si controla vencimiento, FIFO si no)
    getProductStrategy(product) {
      if (!product) return 'fifo'
      return product.picking_strategy || (product.requires_expiry_control ? 'fefo' : 'fifo')
    },

    // Cargar items disponibles cuando se selecciona un producto
    async onProductSelected(line, index, productId) {
      // Llamar al método original
      const product = this.productsWithInventory.find(p => p.id === productId)
      if (product) {
        line.unit_price = product.unit_price || 0
        line.max_quantity = product.available_quantity
        if (line.quantity > product.available_quantity) {
          line.quantity = product.available_quantity
        }
        this.calculateLineTotal(line)

        // Estrategia heredada del producto (salvo override avanzado activo)
        if (!this.mostrarOverrideEstrategia || this.pickingStrategy.level === 'product') {
          line.picking_strategy = this.getProductStrategy(product)
        }
      }
      
      // NUEVO: Cargar items disponibles para picking granular
      if (productId) {
        // En modo "Por Producto", siempre cargar datos para permitir selección individual
        // En modo "Por Orden", cargar si es necesario para la estrategia
        const shouldLoadItems = this.pickingStrategy.level === 'product' || 
                                this.showCriteriaSelectors(line, index) || 
                                (this.pickingStrategy.strategy && this.pickingStrategy.strategy !== 'manual')
        
        if (shouldLoadItems) {
          await this.loadAvailableItemsForLine(line, index, productId)
        }
      }
    },
    
    // Cargar items disponibles para una línea específica
    async loadAvailableItemsForLine(line, index, productId) {
      try {
        console.log(`🔍 Loading available items for product ${productId}`)
        
        // Verificar cache primero
        if (this.availableItemsCache[productId]) {
          this.populateLineWithAvailableItems(line, this.availableItemsCache[productId])
          return
        }
        
        const response = await api.getProductAvailableItems(productId)
        const data = response.data
        
        // Guardar en cache
        this.availableItemsCache[productId] = data
        
        // Poblar la línea con los datos
        this.populateLineWithAvailableItems(line, data)
        
        // Validar compatibilidad con estrategia
        this.validateLineForStrategy(line, index)
        
        // Si es estrategia automática, aplicarla
        if (this.pickingStrategy.level === 'order' && this.pickingStrategy.strategy && this.pickingStrategy.strategy !== 'manual') {
          this.applyAutomaticStrategy(line, this.pickingStrategy.strategy, index)
        }
        
      } catch (error) {
        console.error('Error loading available items:', error)
        line.criteria_error = 'Error al cargar criterios de selección'
        line.has_any_criteria = false
      }
    },
    
    // Poblar línea con items disponibles
    populateLineWithAvailableItems(line, data) {
      console.log(`📦 Populating line with available items:`, data)
      
      // Preservar criterio seleccionado previamente
      const previousCriteria = { ...line.picking_criteria }
      
      line.available_lots = data.lots || []
      line.available_serials = data.serials || []
      line.available_expiry_dates = data.expiry_dates || []
      line.has_any_criteria = data.has_any_criteria || false
      
      console.log(`📦 Line populated - has_any_criteria: ${line.has_any_criteria}, lots: ${line.available_lots.length}, serials: ${line.available_serials.length}, expiry: ${line.available_expiry_dates.length}`)
      
      // Restaurar criterio seleccionado si existía
      if (previousCriteria.type && previousCriteria.value) {
        line.picking_criteria = previousCriteria
        console.log(`🔄 Restored previous criteria:`, previousCriteria)
        console.log(`📦 Available options:`, {
          lots: line.available_lots,
          serials: line.available_serials,
          expiry_dates: line.available_expiry_dates
        })
        
        // Actualizar cantidad máxima basada en el criterio seleccionado
        this.updateMaxQuantityForCriteria(line, previousCriteria)
      }
      
      // Marcar lotes ya usados como deshabilitados
      if (line.product_id && this.usedLotsByProduct[line.product_id]) {
        line.available_lots.forEach(lot => {
          lot.disabled = this.usedLotsByProduct[line.product_id].includes(lot.value)
        })
      }
    },
    
    // Actualizar cantidad máxima basada en criterio seleccionado
    updateMaxQuantityForCriteria(line, criteria) {
      let availableList = []
      
      switch (criteria.type) {
        case 'lot':
          availableList = line.available_lots
          break
        case 'serial':
          availableList = line.available_serials
          break
        case 'expiry':
          availableList = line.available_expiry_dates
          break
      }
      
      const selectedItem = availableList.find(item => item.value === criteria.value)
      if (selectedItem) {
        line.max_quantity = selectedItem.available_quantity
        console.log(`📊 Updated max quantity for criteria ${criteria.type}=${criteria.value}: ${selectedItem.available_quantity}`)
      }
    },
    
    // Validar línea para estrategia
    validateLineForStrategy(line, index) {
      // Determinar la estrategia efectiva (global o individual)
      const strategy = this.pickingStrategy.level === 'order' 
        ? this.pickingStrategy.strategy 
        : line.picking_strategy
      
      // Si no hay estrategia definida, no mostrar error
      if (!strategy) {
        line.can_select_criteria = true
        line.criteria_error = null
        return true
      }
      
      if (strategy === 'fefo' && line.available_expiry_dates.length === 0) {
        line.can_select_criteria = false
        line.criteria_error = 'Este producto no tiene fechas de vencimiento para estrategia FEFO'
        return false
      }
      
      if ((strategy === 'fifo' || strategy === 'lifo') && !line.has_any_criteria) {
        line.can_select_criteria = false
        line.criteria_error = 'Este producto no tiene criterios de trazabilidad'
        return false
      }
      
      if (strategy === 'manual' && !line.has_any_criteria) {
        line.can_select_criteria = false
        line.criteria_error = 'Este producto no tiene criterios para selección manual'
        return false
      }
      
      line.can_select_criteria = true
      line.criteria_error = null
      return true
    },
    
    // Aplicar estrategia automática
    applyAutomaticStrategy(line, strategy, lineIndex = null) {
      this.clearLineCriteria(line)
      
      switch (strategy) {
        case 'fifo':
          if (line.available_lots.length > 0) {
            const oldest = line.available_lots[0] // Ya están ordenados por fecha
            line.picking_criteria = {
              type: 'lot',
              value: oldest.value,
              available_quantity: oldest.quantity
            }
            // Limpiar error al aplicar estrategia exitosamente
            line.criteria_error = null
          }
          break
          
        case 'lifo':
          if (line.available_lots.length > 0) {
            const newest = line.available_lots[line.available_lots.length - 1]
            line.picking_criteria = {
              type: 'lot',
              value: newest.value,
              available_quantity: newest.quantity
            }
            // Limpiar error al aplicar estrategia exitosamente
            line.criteria_error = null
          }
          break
          
        case 'fefo':
          if (line.available_expiry_dates.length > 0) {
            const soonest = line.available_expiry_dates[0] // Ya están ordenados
            line.picking_criteria = {
              type: 'expiry',
              value: soonest.value,
              available_quantity: soonest.quantity
            }
            // Limpiar error al aplicar estrategia exitosamente
            line.criteria_error = null
          }
          break
      }
      
      // Actualizar lotes usados si es necesario
      if (line.picking_criteria.type === 'lot' && line.picking_criteria.value && lineIndex !== null) {
        this.updateUsedLots(line.product_id, lineIndex, line.picking_criteria.value)
      }
    },
    
    // Obtener opciones de estrategia para un producto específico
    getStrategyOptionsForProduct(line) {
      const baseOptions = [
        { 
          value: 'fifo', 
          text: 'FIFO (Primero en Entrar, Primero en Salir)', 
          description: 'Selecciona automáticamente por fecha de ingreso más antigua'
        },
        { 
          value: 'lifo', 
          text: 'LIFO (Último en Entrar, Primero en Salir)', 
          description: 'Selecciona automáticamente por fecha de ingreso más reciente'
        },
        { 
          value: 'manual', 
          text: 'Selección Manual', 
          description: 'Permite seleccionar manualmente lote, S/N o fecha de vencimiento específicos'
        }
      ]
      
      // Solo agregar FEFO si el producto tiene fechas de vencimiento
      if (line.has_any_criteria && line.available_expiry_dates && line.available_expiry_dates.length > 0) {
        baseOptions.splice(2, 0, {
          value: 'fefo',
          text: 'FEFO (Primero en Vencer, Primero en Salir)',
          description: 'Selecciona automáticamente por fecha de vencimiento más próxima'
        })
      }
      
      return baseOptions
    },
    
    // Manejar cambios en la estrategia individual de un producto
    async onProductStrategyChanged(lineIndex, strategy) {
      console.log(`🎯 Product strategy changed for line ${lineIndex}:`, strategy)
      
      const line = this.orderForm.order_lines[lineIndex]
      if (!line) return
      
      console.log(`🎯 Line before strategy change - product_id: ${line.product_id}, has_any_criteria: ${line.has_any_criteria}`)
      
      line.picking_strategy = strategy
      
      // Asegurar que los datos estén cargados antes de aplicar cualquier estrategia
      if (line.product_id && strategy) {
        // Verificar si los datos ya fueron cargados (cualquier array con contenido indica que se cargó)
        const dataAlreadyLoaded = line.available_lots.length > 0 || 
                                 line.available_serials.length > 0 || 
                                 line.available_expiry_dates.length > 0 ||
                                 line.has_any_criteria === true
        
        if (!dataAlreadyLoaded) {
          console.log(`🎯 Loading items for line ${lineIndex} - first time loading data`)
          await this.loadAvailableItemsForLine(line, lineIndex, line.product_id)
        }
      }
      
      if (strategy === 'manual') {
        // Si es manual, limpiar criterios y permitir selección manual
        this.clearLineCriteria(line)
      } else if (strategy) {
        // Si es una estrategia automática, aplicarla
        this.applyAutomaticStrategy(line, strategy, lineIndex)
      } else {
        // Si se limpia la estrategia, limpiar criterios
        this.clearLineCriteria(line)
      }
    },

    // Obtener información del producto seleccionado según la estrategia
    getSelectedProductInfo(line) {
      if (!line.product_id) return null
      
      // Determinar la estrategia efectiva
      const effectiveStrategy = this.pickingStrategy.level === 'order' 
        ? this.pickingStrategy.strategy 
        : line.picking_strategy
      
      if (!effectiveStrategy) return null
      
      const info = {
        strategyName: this.getStrategyDisplayName(effectiveStrategy),
        criteriaInfo: null,
        criteriaLabel: null,
        dateInfo: null,
        dateLabel: null,
        location: null
      }
      
      // Para estrategias automáticas, buscar el item seleccionado automáticamente
      if (effectiveStrategy !== 'manual') {
        const selectedItem = this.getAutoSelectedItem(line, effectiveStrategy)
        if (selectedItem) {
          this.populateInfoFromSelectedItem(info, selectedItem, effectiveStrategy)
        }
      } 
      // Para estrategia manual, usar los criterios seleccionados manualmente
      else if (line.picking_criteria && line.picking_criteria.type && line.picking_criteria.value) {
        const selectedItem = this.getManualSelectedItem(line)
        if (selectedItem) {
          this.populateInfoFromSelectedItem(info, selectedItem, 'manual', line.picking_criteria.type)
        }
      }
      
      // Solo mostrar si hay información relevante
      return (info.criteriaInfo || info.dateInfo || info.location) ? info : null
    },
    
    // Obtener nombre de estrategia para mostrar
    getStrategyDisplayName(strategy) {
      const strategyNames = {
        'fifo': 'FIFO (Primero en Entrar)',
        'lifo': 'LIFO (Último en Entrar)', 
        'fefo': 'FEFO (Primero en Vencer)',
        'manual': 'Selección Manual'
      }
      return strategyNames[strategy] || strategy
    },
    
    // Obtener item seleccionado automáticamente según estrategia
    getAutoSelectedItem(line, strategy) {
      switch (strategy) {
        case 'fifo':
          // El primer item de available_lots (ya ordenado por fecha de ingreso)
          return line.available_lots && line.available_lots.length > 0 ? line.available_lots[0] : null
          
        case 'lifo':
          // El último item de available_lots 
          return line.available_lots && line.available_lots.length > 0 
            ? line.available_lots[line.available_lots.length - 1] : null
            
        case 'fefo':
          // El primer item de available_expiry_dates (ya ordenado por fecha de vencimiento)
          return line.available_expiry_dates && line.available_expiry_dates.length > 0 
            ? line.available_expiry_dates[0] : null
            
        default:
          return null
      }
    },
    
    // Obtener item seleccionado manualmente
    getManualSelectedItem(line) {
      const criteria = line.picking_criteria
      if (!criteria.type || !criteria.value) return null
      
      switch (criteria.type) {
        case 'lot':
          return line.available_lots ? line.available_lots.find(item => item.value === criteria.value) : null
        case 'serial':
          return line.available_serials ? line.available_serials.find(item => item.value === criteria.value) : null
        case 'expiry':
          return line.available_expiry_dates ? line.available_expiry_dates.find(item => item.value === criteria.value) : null
        default:
          return null
      }
    },
    
    // Poblar información desde el item seleccionado
    populateInfoFromSelectedItem(info, selectedItem, strategy, manualCriteriaType = null) {
      if (!selectedItem) return
      
      // Información de ubicación (siempre disponible)
      if (selectedItem.location_code) {
        info.location = selectedItem.location_description 
          ? `${selectedItem.location_code} - ${selectedItem.location_description}`
          : selectedItem.location_code
      }
      
      // Información específica según estrategia
      if (strategy === 'fifo' || strategy === 'lifo') {
        info.dateInfo = selectedItem.entry_date ? this.formatDate(selectedItem.entry_date) : 'No disponible'
        info.dateLabel = 'Fecha de Ingreso'
        
        if (selectedItem.lot_number) {
          info.criteriaInfo = selectedItem.lot_number
          info.criteriaLabel = 'Lote'
        }
      } 
      else if (strategy === 'fefo') {
        info.dateInfo = selectedItem.expiry_date ? this.formatDate(selectedItem.expiry_date) : 'No disponible'
        info.dateLabel = 'Fecha de Vencimiento'
        
        if (selectedItem.lot_number) {
          info.criteriaInfo = selectedItem.lot_number
          info.criteriaLabel = 'Lote'
        }
      }
      else if (strategy === 'manual' && manualCriteriaType) {
        if (manualCriteriaType === 'lot') {
          info.criteriaInfo = selectedItem.lot_number || selectedItem.value
          info.criteriaLabel = 'Lote'
          
          if (selectedItem.entry_date) {
            info.dateInfo = this.formatDate(selectedItem.entry_date)
            info.dateLabel = 'Fecha de Ingreso'
          }
        }
        else if (manualCriteriaType === 'serial') {
          info.criteriaInfo = selectedItem.serial_number || selectedItem.value
          info.criteriaLabel = 'Número de Serie'
          
          if (selectedItem.entry_date) {
            info.dateInfo = this.formatDate(selectedItem.entry_date)
            info.dateLabel = 'Fecha de Ingreso'
          }
        }
        else if (manualCriteriaType === 'expiry') {
          info.dateInfo = selectedItem.expiry_date ? this.formatDate(selectedItem.expiry_date) : selectedItem.value
          info.dateLabel = 'Fecha de Vencimiento'
          
          if (selectedItem.lot_number) {
            info.criteriaInfo = selectedItem.lot_number
            info.criteriaLabel = 'Lote'
          }
        }
      }
    },
    
    // Formatear fecha para mostrar
    formatDate(dateString) {
      if (!dateString) return 'No disponible'
      try {
        const date = new Date(dateString)
        return date.toLocaleDateString('es-CL', {
          year: 'numeric',
          month: '2-digit', 
          day: '2-digit'
        })
      } catch (e) {
        return dateString
      }
    },

    // Obtener hint para el campo de cantidad
    getQuantityHint(line) {
      if (!line.max_quantity) return ''
      
      // Si hay un criterio seleccionado, mostrar información específica
      if (line.picking_criteria && line.picking_criteria.type && line.picking_criteria.value) {
        const criteriaType = line.picking_criteria.type === 'lot' ? 'Lote' :
                           line.picking_criteria.type === 'serial' ? 'S/N' :
                           line.picking_criteria.type === 'expiry' ? 'Vencimiento' : 'Criterio'
        return `Máximo para ${criteriaType} seleccionado: ${line.max_quantity} unidades`
      }
      
      // Hint normal cuando no hay criterio específico
      return `Máximo disponible: ${line.max_quantity} unidades`
    },
    
    // Limpiar criterios de una línea
    clearLineCriteria(line) {
      line.picking_criteria = {
        type: null,
        value: null,
        available_quantity: 0
      }
      
      // Limpiar errores de criterios
      line.criteria_error = null
      
      // Restaurar cantidad máxima al stock total del producto
      if (line.product_id) {
        const product = this.productsWithInventory.find(p => p.id === line.product_id)
        if (product) {
          line.max_quantity = product.available_quantity
        }
      }
    },
    
    // Manejar cambios en criterios
    onCriteriaChanged(index, event) {
      console.log(`🔍 Criteria changed for line ${index}:`, event)
      
      const line = this.orderForm.order_lines[index]
      if (!line) return
      
      if (event.type === 'value-changed') {
        // Limpiar error cuando se selecciona un criterio válido
        line.criteria_error = null
        
        // Actualizar cantidad máxima disponible basada en el criterio seleccionado
        if (event.availableQuantity !== undefined) {
          line.max_quantity = event.availableQuantity
          
          // Si la cantidad actual excede la disponible, ajustarla automáticamente
          if (line.quantity > event.availableQuantity) {
            line.quantity = event.availableQuantity
            this.calculateLineTotal(line)
          }
        }
        
        // Manejar lotes usados para evitar duplicados
        if (event.criteriaType === 'lot') {
          this.updateUsedLots(line.product_id, index, event.criteriaValue)
        }
        
      } else if (event.type === 'type-changed') {
        // Cuando cambia el tipo de criterio, resetear la cantidad máxima al stock total
        const product = this.productsWithInventory.find(p => p.id === line.product_id)
        line.max_quantity = product ? product.available_quantity : null
        
        // Limpiar criterios previos
        line.picking_criteria = {
          type: event.criteriaType,
          value: null,
          available_quantity: 0
        }
      }
    },
    
    // Control de lotes usados
    getUsedLots(productId, currentIndex) {
      if (!this.usedLotsByProduct[productId]) return []
      
      return this.usedLotsByProduct[productId]
        .map((lot, index) => ({ lot, index }))
        .filter(item => item.index !== currentIndex)
        .map(item => item.lot)
    },
    
    // Actualizar lotes usados
    updateUsedLots(productId, index, lotValue) {
      if (!this.usedLotsByProduct[productId]) {
        this.usedLotsByProduct[productId] = []
      }
      this.usedLotsByProduct[productId][index] = lotValue
      
      // Actualizar disponibilidad en otras líneas del mismo producto
      this.orderForm.order_lines.forEach((line, lineIndex) => {
        if (line.product_id === productId && lineIndex !== index) {
          line.available_lots.forEach(lot => {
            lot.disabled = this.usedLotsByProduct[productId].includes(lot.value)
          })
        }
      })
    },
    
    // ==================== FIN PICKING GRANULAR ====================
    
    // Operaciones de orden
    async allocateOrder(order) {
      try {
        console.log(`🔄 Allocating order ${order.id}`)
        
        // Intentar obtener criterios granulares existentes
        let allocationData = null
        
        try {
          const criteriaResponse = await api.getOrderGranularCriteria(order.id)
          const { picking_strategy_config, granular_criteria, has_allocations } = criteriaResponse.data
          
          if (has_allocations || Object.keys(granular_criteria).length > 0) {
            // Si tiene criterios granulares, usarlos para la asignación
            allocationData = {
              granular_criteria: granular_criteria,
              picking_strategy: picking_strategy_config
            }
            console.log(`📋 Using existing granular criteria:`, allocationData)
          }
        } catch (criteriaError) {
          console.warn('No granular criteria found, using standard allocation:', criteriaError)
        }
        
        // Llamar al endpoint de asignación
        const response = await api.allocateOrder(order.id, allocationData)
        
        if (response.data.success) {
          if (response.data.fully_allocated) {
            this.showSnackbar('Inventario asignado exitosamente', 'success')
          } else {
            // Mostrar detalles de asignación parcial
            const partialResults = response.data.allocation_results || []
            const failedItems = partialResults.filter(r => !r.success)
            
            if (failedItems.length > 0) {
              const failedProducts = failedItems.map(item => item.product_name).join(', ')
              this.showSnackbar(`Asignación parcial: Sin inventario suficiente para ${failedProducts}`, 'warning')
            } else {
              this.showSnackbar('Asignación parcial completada', 'warning')
            }
          }
        } else {
          this.showSnackbar(`Error en asignación: ${response.data.error || 'Error desconocido'}`, 'error')
        }
        
        await this.loadOrders()
        await this.loadDashboardStats()
        
      } catch (error) {
        console.error('Error allocating order:', error)
        
        // Manejar diferentes tipos de errores
        if (error.response) {
          const errorMsg = error.response.data?.error || error.response.data?.message || 'Error del servidor'
          if (errorMsg.includes('inventario') || errorMsg.includes('disponible')) {
            this.showSnackbar(`Conflicto de inventario: ${errorMsg}`, 'error')
          } else {
            this.showSnackbar(`Error al asignar inventario: ${errorMsg}`, 'error')
          }
        } else {
          this.showSnackbar('Error de conexión al asignar inventario', 'error')
        }
      }
    },
    
    async releaseAllocations(order) {
      try {
        await api.releaseAllocations(order.id)
        this.showSnackbar('Asignaciones liberadas exitosamente')
        await this.loadOrders()
        await this.loadDashboardStats()
      } catch (error) {
        this.showSnackbar('Error al liberar asignaciones', 'error')
        console.error('Error releasing allocations:', error)
      }
    },
    
    async createPickingList(order) {
      try {
        await api.createPickingList(order.id)
        this.showSnackbar('Lista de picking creada exitosamente')
        await this.loadOrders()
        await this.loadDashboardStats()
      } catch (error) {
        this.showSnackbar('Error al crear lista de picking', 'error')
        console.error('Error creating picking list:', error)
      }
    },
    
    async createShipment(order) {
      try {
        const shipmentData = {
          carrier: 'Transportista Estándar',
          tracking_number: `TRK${Date.now()}`,
          service_type: 'standard'
        }
        await api.createShipment(order.id, shipmentData)
        this.showSnackbar('Envío creado exitosamente')
        await this.loadOrders()
        await this.loadDashboardStats()
      } catch (error) {
        this.showSnackbar('Error al crear envío', 'error')
        console.error('Error creating shipment:', error)
      }
    },
    
    // Utilidades
    getStatusColor(status) {
      const colors = {
        pending: 'orange',
        assigned: 'blue',
        picking: 'purple',
        picked: 'indigo',
        packed: 'teal',
        shipped: 'green',
        delivered: 'success',
        cancelled: 'red'
      }
      return colors[status] || 'grey'
    },
    
    getStatusText(status) {
      const texts = {
        pending: 'Pendiente',
        assigned: 'Asignado',
        picking: 'Recolectando',
        picked: 'Recolectado',
        packed: 'Empacado',
        shipped: 'Pickeado',
        delivered: 'Entregado',
        cancelled: 'Cancelado'
      }
      return texts[status] || status
    },
    
    formatCurrency(amount) {
      return new Intl.NumberFormat('es-CL', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount || 0)
    },
    
    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString('es-CL')
    },
    
    showSnackbar(message, color = 'success') {
      this.snackbar = {
        show: true,
        message,
        color
      }
    },
    
    // Customer search
    searchCustomers(search) {
      this.customerSearch = search || ''
      if (!this.customerSearch) {
        this.filteredCustomers = this.customers
        return
      }
      
      const searchLower = this.customerSearch.toLowerCase()
      this.filteredCustomers = this.customers.filter(customer =>
        customer.name.toLowerCase().includes(searchLower) ||
        customer.tax_id.toLowerCase().includes(searchLower)
      )
    },
    
    onCustomerSelected(customer) {
      // La computed property selectedCustomer ya maneja la conversión
      // Este método se mantiene para compatibilidad con otros eventos
      if (customer && customer.id) {
        this.orderForm.customer_id = customer.id
      } else if (customer === null || customer === undefined) {
        this.orderForm.customer_id = null
      }
    },
    
    cancelCustomerDialog() {
      this.showCreateCustomerDialog = false
      this.customerForm = {
        name: '',
        tax_id: '',
        contact_person: '',
        contact_phone: '',
        contact_email: '',
        city: '',
        address: '',
        country: '',
        postal_code: '',
        payment_terms: ''
      }
    },
    
    async saveCustomer() {
      if (!this.customerFormValid) return
      
      try {
        const response = await api.createCustomer(this.customerForm)
        const newCustomer = response.data
        
        this.showSnackbar('Cliente creado exitosamente')
        this.cancelCustomerDialog()
        
        // Recargar clientes y seleccionar el nuevo
        await this.loadCustomers()
        this.orderForm.customer_id = newCustomer.id
        
      } catch (error) {
        this.showSnackbar(error.response?.data?.error || 'Error al crear cliente', 'error')
        console.error('Error creating customer:', error)
      }
    }
  }
}
</script>

<style scoped>
.orders-container {
  padding: 20px;
}

.stats-card {
  height: 100%;
}

.orders-table {
  background-color: white;
}

.orders-table >>> .v-data-table__wrapper {
  border-radius: 8px;
}

.orders-table >>> th {
  background-color: #f8f9fa !important;
  font-weight: bold !important;
  border-bottom: 2px solid #dee2e6 !important;
}

.orders-table >>> td {
  border-bottom: 1px solid #dee2e6 !important;
  padding: 12px 16px !important;
}

.orders-table >>> tr:hover {
  background-color: #f8f9fa !important;
}

/* Estilos para el flex con gap */
.d-flex.align-center.gap-2 {
  gap: 8px;
}

.d-flex.align-center.gap-2 > * {
  flex-shrink: 0;
}

.d-flex.align-center.gap-2 > .v-autocomplete {
  flex: 1;
}

/* Responsive Design */
@media (max-width: 768px) {
  .orders-container {
    padding: 15px;
  }
  
  /* Header responsivo */
  .orders-header {
    flex-direction: column;
    align-items: stretch !important;
    gap: 15px;
  }
  
  .orders-header h1 {
    font-size: 1.5rem !important;
    text-align: center;
  }
  
  .orders-create-btn {
    width: 100%;
    justify-content: center;
  }
  
  /* Filtros responsivos */
  .orders-filters .v-card-text {
    padding: 12px !important;
  }
  
  /* Stats cards en móvil */
  .stats-card .v-card-text {
    padding: 12px !important;
  }
  
  .stats-card .text-h4 {
    font-size: 1.5rem !important;
  }
  
  .stats-card .text-subtitle-2 {
    font-size: 0.75rem !important;
  }
}

@media (max-width: 480px) {
  .orders-container {
    padding: 10px;
  }
  
  /* Header extra compacto */
  .orders-header h1 {
    font-size: 1.25rem !important;
  }
  
  /* Stats cards más compactos */
  .stats-card .v-card-text {
    padding: 8px !important;
  }
  
  .stats-card .v-icon {
    margin-right: 8px !important;
  }
  
  .stats-card .text-h4 {
    font-size: 1.25rem !important;
  }
  
  /* Filtros más compactos */
  .orders-filters .v-card-text {
    padding: 8px !important;
  }
}
</style> 